import { getDragonSprite, getDragonVector } from "./assets";
import {
  blitSprite,
  parseSprite,
  snap,
  type Palette,
  type Sprite,
} from "./blit";
import { drawVectorSprite, vectorContentBottom } from "./vectorPixel";
export type DragonPhase = "incoming" | "waiting" | "boarding" | "departing";

export interface SeaDragon {
  hash: string;
  height: number;
  /** When fanout coalesced several tips onto one dragon (HUD/label). */
  heights?: number[];
  x: number;
  y: number;
  phase: DragonPhase;
  born: number;
  /** When this dragon arrived at the dock (0 until then). */
  dockedAt: number;
  /** Palette / PNG family 0–4. */
  color: number;
  riderId: string | null;
  passengers: number;
}

/** Fallback diamond-fish if PNG bake hasn't finished. */
const DRAGON_FALLBACK = parseSprite([
  "................................................",
  "..........AA....................................",
  ".........ALLA..............A....................",
  "........ALLLLA............ALA...................",
  ".......OHLLLLDO..........ALALA..................",
  "......OHHLLLLDDO...OOOOOALALALO.................",
  ".....OHHHKKKKDDDOOOMMMMMMMMMMMAO................",
  "....OHHHKKPKKDDDMMMMMMMMMMMMMMMAO...............",
  "....OHHHKKKKKDDMMMMMLLLLLLLMMMMMAO..............",
  ".....OPPOOOOOOMMMMLLLLLLLLLLMMMMMAO...AA........",
  "......OO.....OMMMMLLLUUULLLLMMMMMMMO.ALA........",
  ".............OODDDLLUIIULLLDDDMMMMMOALALO.......",
  "..............OODDDDUUIIUUDDDDDMMMMOALALO.......",
  "...............OODDDUUUUUUDDDDDDDMMO.ALA........",
  "................OODDDDDDDDDDDDDDDOO...A.........",
  ".................O..O..O..O..O..O...............",
  "................................................",
  "................................................",
]);

const DRAGON_PALETTES: Palette[] = [
  {
    O: "#142028",
    D: "#1a5a62",
    M: "#2a8a8a",
    L: "#5ec8c0",
    H: "#a8ece4",
    A: "#3aa090",
    U: "#c8f0e8",
    I: "#79d6cb",
    K: "#f2c8a8",
    P: "#1a1020",
  },
  {
    O: "#201418",
    D: "#8a3040",
    M: "#d05060",
    L: "#f08090",
    H: "#ffc0c8",
    A: "#e07050",
    U: "#ffe8e0",
    I: "#79d6cb",
    K: "#f2c8a8",
    P: "#1a1020",
  },
  {
    O: "#141020",
    D: "#3a2868",
    M: "#6850b0",
    L: "#9880e0",
    H: "#d0c0ff",
    A: "#c070e0",
    U: "#f0e8ff",
    I: "#79d6cb",
    K: "#f2c8a8",
    P: "#1a1020",
  },
  {
    O: "#201808",
    D: "#8a6820",
    M: "#d0a030",
    L: "#f0d060",
    H: "#fff0a8",
    A: "#f09030",
    U: "#fff8e0",
    I: "#79d6cb",
    K: "#f2c8a8",
    P: "#1a1020",
  },
  {
    O: "#101810",
    D: "#2a5830",
    M: "#489048",
    L: "#78c068",
    H: "#b8e8a0",
    A: "#e0c040",
    U: "#e8f8e0",
    I: "#79d6cb",
    K: "#f2c8a8",
    P: "#1a1020",
  },
];

export function dragonColorFromHash(hash: string): number {
  let n = 0;
  for (let i = 0; i < Math.min(8, hash.length); i++) {
    n = (n * 33 + hash.charCodeAt(i)) >>> 0;
  }
  return n % DRAGON_PALETTES.length;
}

function dragonDrawSize(d: SeaDragon): { w: number; h: number } {
  const vec = getDragonVector(d.color);
  if (vec) return { w: vec.w, h: vec.h };
  const png = getDragonSprite(d.color);
  if (png) return { w: png.width, h: png.height };
  return { w: DRAGON_FALLBACK.w, h: DRAGON_FALLBACK.h };
}

/** Seat on the mid-back — where a rider sits. */
export function dragonSeat(d: SeaDragon): { x: number; y: number } {
  const facingLeft = d.phase !== "departing";
  const { w, h } = dragonDrawSize(d);
  // Meet the creature path near the withers, slightly shore-side when waiting.
  return {
    x: snap(d.x + (facingLeft ? Math.max(2, Math.floor(w * 0.08)) : -Math.max(2, Math.floor(w * 0.08)))),
    y: snap(d.y - Math.max(3, Math.floor(h * 0.22))),
  };
}

function wobbleY(d: SeaDragon, time: number): number {
  if (d.phase === "waiting" || d.phase === "boarding") {
    return Math.round(Math.sin(time * 5 + d.height) * 1);
  }
  return Math.round(Math.sin(time * 8 + d.x * 0.05) * 1);
}

export function drawDragonSprite(
  ctx: CanvasRenderingContext2D,
  d: SeaDragon,
  time: number,
): void {
  const wobble = wobbleY(d, time);
  const flipX = d.phase === "departing";
  const vec = getDragonVector(d.color);

  ctx.save();
  ctx.imageSmoothingEnabled = false;

  if (vec) {
    const x = snap(d.x - vec.w / 2);
    const y = snap(d.y - vec.h / 2 + wobble);
    drawVectorSprite(ctx, vec, x, y, { flipX });
  } else {
    const frame: Sprite = DRAGON_FALLBACK;
    const palette = DRAGON_PALETTES[d.color % DRAGON_PALETTES.length]!;
    const x = snap(d.x - frame.w / 2);
    const y = snap(d.y - frame.h / 2 + wobble);
    blitSprite(ctx, frame, palette, x, y, 1, { flipX });
  }
  ctx.restore();
}

/** Label just under the dragon sprite (~1–2px below the drawn belly). */
export function dragonLabelAnchor(
  d: SeaDragon,
  time: number,
): { x: number; y: number } {
  const { h } = dragonDrawSize(d);
  const wobble = wobbleY(d, time);
  const vec = getDragonVector(d.color);
  const belly = vec
    ? -Math.floor(h / 2) + vectorContentBottom(vec)
    : Math.ceil(h / 2);
  return {
    x: snap(d.x),
    y: snap(d.y + belly + wobble + 1),
  };
}

/** Single tip height for HUD / toast / labels (never truncate synthetic hashes). */
export function formatHeight(height: number): string {
  if (!(height > 0) || height >= 1_000_000_000) return "";
  return `#${height.toLocaleString("en-US")}`;
}

/**
 * HUD "latest block" — always one tip. Coalesced fanout lists must not
 * grow the panel; dragon overlay labels may still show a range.
 */
export function formatLatestHeight(heights: number[]): string {
  const usable = heights.filter((h) => h > 0 && h < 1_000_000_000);
  if (usable.length === 0) return "";
  return formatHeight(Math.max(...usable))!;
}

/** Format one or more block heights for dragon overlay labels. */
export function formatBlockHeights(heights: number[]): string {
  const usable = heights.filter((h) => h > 0 && h < 1_000_000_000);
  if (usable.length === 0) return "";
  if (usable.length === 1) return formatHeight(usable[0]!)!;
  // Keep labels readable under burst pressure.
  if (usable.length > 4) {
    const first = usable[0]!;
    const last = usable[usable.length - 1]!;
    return `${formatHeight(first)}–${formatHeight(last)}`;
  }
  return usable.map((h) => formatHeight(h)).join(", ");
}

/** Prefer numeric tip(s); never show synthetic `height:…` hash stubs. */
export function dragonHeightLabel(d: SeaDragon): string {
  const fromMulti = formatBlockHeights(d.heights ?? []);
  if (fromMulti) return fromMulti;
  const single = formatHeight(d.height);
  if (single) return single;
  if (d.hash.startsWith("height:")) {
    const n = Number(d.hash.slice("height:".length));
    return formatHeight(n) || "block";
  }
  return "block";
}

/** Short id for the HTML overlay (block height preferred). */
export function dragonLabelText(d: SeaDragon): string {
  return dragonHeightLabel(d);
}

export const DRAGON_SPRITE_SIZE = { w: 96, h: 64 };
